import { ApolloError } from '@apollo/client'
import { GraphQLError } from 'graphql'
import type { UseFormMethods } from 'react-hook-form'

import { CartFragment, SetPaymentMethodOnCartMutationVariables } from '../../graphql/magento'
import { CardExpiredError, CardTypeInvalidError, isCartUpdateError } from './errors'
import { CustomerAccountInfoFromQuery, FormValues, ValidFormValues } from './types'

export const requiredCreditCardFieldValues = [
  'cardCVV',
  'cardExpMonth',
  'cardExpYear',
  'cardNumber',
  'cardType',
  'saveCard',
  'subscriptionId',
] as const

export const isCreditCard = (
  values: ValidFormValues.PaymentDetails,
): values is ValidFormValues.CreditCard =>
  values.code === 'paradoxlabs_cybersource' && 'cardNumber' in values

export const isFree = (
  values: ValidFormValues.PaymentDetails | ValidFormValues.NoPaymentDetailsRequired,
): values is ValidFormValues.NoPaymentDetailsRequired => values.code === 'free'

export const isPurchaseOrder = (
  values: ValidFormValues.PaymentDetails,
): values is ValidFormValues.PurchaseOrder => values.code === 'purchaseorder'

export const isTruemed = (
  values: ValidFormValues.PaymentDetails,
): values is ValidFormValues.Truemed => values.code === 'truemed'

export const isValidCardExpiration = (cardExpMonth: string, cardExpYear: string) => {
  const date = new Date()
  const currentMonthYear = new Date(date.getFullYear(), date.getMonth())
  return currentMonthYear <= new Date(Number(cardExpYear), Number(cardExpMonth) - 1)
}

export function assertValidCardExpiration<
  TValues extends { cardExpMonth?: string | undefined; cardExpYear?: string | undefined },
>(
  values: Pick<TValues, 'cardExpMonth' | 'cardExpYear'> &
    Omit<TValues, 'cardExpMonth' | 'cardExpYear'>,
): asserts values is {
  cardExpMonth: ValidFormValues.CreditCard['cardExpMonth']
  cardExpYear: ValidFormValues.CreditCard['cardExpYear']
} & Omit<TValues, 'cardExpMonth' | 'cardExpYear'> {
  const { cardExpMonth, cardExpYear } = values
  if (
    typeof cardExpMonth !== 'string' ||
    typeof cardExpYear !== 'string' ||
    !isValidCardExpiration(cardExpMonth, cardExpYear)
  ) {
    throw new CardExpiredError(`Invalid card expiration: ${JSON.stringify(values)}`)
  }
}

export const isValidCardType = (
  cardType: string,
): cardType is ValidFormValues.CreditCard['cardType'] =>
  cardType === 'AE' || cardType === 'DI' || cardType === 'MC' || cardType === 'VI'

export function assertValidCardType<TValues extends { cardType?: string | undefined }>(
  values: Pick<TValues, 'cardType'> & Omit<TValues, 'cardType'>,
): asserts values is {
  cardType: ValidFormValues.CreditCard['cardType']
} & Omit<TValues, 'cardType'> {
  const { cardType } = values
  if (typeof cardType !== 'string' || !isValidCardType(cardType)) {
    throw new CardTypeInvalidError(`Invalid cardType: ${JSON.stringify(cardType)}`)
  }
}

export const mapCodeFromPaymentMethod = (
  paymentMethodCode?: string,
): ValidFormValues.PaymentDetails['code'] => {
  switch (paymentMethodCode) {
    case 'paradoxlabs_cybersource':
      return 'paradoxlabs_cybersource'
    case 'purchaseorder':
      return 'purchaseorder'
    case 'designsforhealth_truemed':
      return 'truemed'
    default:
      return 'paradoxlabs_cybersource'
  }
}

const mapCodeFromCartSelectedPaymentMethod = (
  selectedPaymentMethod: CartFragment['selected_payment_method'],
): ValidFormValues.PaymentDetails['code'] => mapCodeFromPaymentMethod(selectedPaymentMethod?.code)

export const codeFromCartSelectedPaymentMethod = ({
  selectedPaymentMethod,
  showPurchaseOrderOption,
  showTruemedOption,
}: {
  selectedPaymentMethod: CartFragment['selected_payment_method']
  showPurchaseOrderOption: boolean
  showTruemedOption: boolean
}): ValidFormValues.PaymentDetails['code'] => {
  const code = mapCodeFromCartSelectedPaymentMethod(selectedPaymentMethod)
  if (
    (!showPurchaseOrderOption && code === 'purchaseorder') ||
    (!showTruemedOption && code === 'truemed')
  ) {
    return 'paradoxlabs_cybersource'
  }
  return code
}

export const defaultPaymentDetailsValues = ({
  billingAddress,
  paymentMethodCode,
  selectedPaymentMethod,
}: {
  billingAddress: FormValues.Address
  paymentMethodCode: ValidFormValues.PaymentDetails['code']
  selectedPaymentMethod: CartFragment['selected_payment_method']
}): FormValues.PaymentDetails => {
  const purchaseOrderNumber = selectedPaymentMethod?.purchase_order_number
  return {
    ...billingAddress,
    ...(paymentMethodCode === 'purchaseorder' && {
      purchaseOrderNumber:
        purchaseOrderNumber && purchaseOrderNumber !== 'N/A' ? purchaseOrderNumber : undefined,
    }),
  }
}

export const paymentMethodFromFieldValues = (
  values: ValidFormValues.PaymentDetails | ValidFormValues.NoPaymentDetailsRequired,
): SetPaymentMethodOnCartMutationVariables['paymentMethod'] => {
  if (isFree(values)) {
    return {
      code: 'free',
    }
  }

  if (isPurchaseOrder(values)) {
    return {
      code: 'purchaseorder',
      purchase_order_number: values.purchaseOrderNumber || 'N/A',
    }
  }

  if (isTruemed(values)) {
    return {
      code: 'designsforhealth_truemed',
    }
  }

  return {
    code: 'paradoxlabs_cybersource',
    tokenbase_data: {
      card_id: values.subscriptionId,
      cc_cid: values.cardCVV,
      // always set `saveCard` to true if using saved card
      save: values.selectedSavedCreditCardId ? true : values.saveCard,
    },
  }
}

export const availableAccountCredit = (accountInfo: CustomerAccountInfoFromQuery): number => {
  if (!accountInfo) {
    return 0
  }

  return accountInfo.creditLimit - accountInfo.balanceOwed
}

export const daysAccountOverdue = (accountInfo: CustomerAccountInfoFromQuery): number => {
  if (!accountInfo) {
    return 0
  }

  return accountInfo.daysOverdue
}

const updatePaymentMethodErrorsFrom = (err: Error): Readonly<GraphQLError[]> | undefined => {
  if (!isCartUpdateError(err) || err.operation !== 'updatePaymentMethod') {
    return undefined
  }
  return err.cause instanceof ApolloError ? err.cause.graphQLErrors : undefined
}

export const isPaymentError = (err: Error): boolean => {
  if (err instanceof CardTypeInvalidError) {
    return true
  }
  const paymentErrors = updatePaymentMethodErrorsFrom(err)
  return paymentErrors != null && paymentErrors.length > 0
}

export const setErrorStateOnFields = ({
  error,
  getValues,
  selectedPaymentMethod,
  setError,
}: {
  error: Error
  getValues: UseFormMethods<FormValues.PaymentDetails>['setError']
  selectedPaymentMethod: ValidFormValues.PaymentDetails['code']
  setError: UseFormMethods<FormValues.PaymentDetails>['setError']
}): void => {
  const paymentErrors = updatePaymentMethodErrorsFrom(error)
  if (!paymentErrors || selectedPaymentMethod !== 'paradoxlabs_cybersource') {
    return
  }

  // cast useForm methods to required types
  const getCreditCardValues = getValues as UseFormMethods<
    FormValues.CreditCard | FormValues.SavedCreditCard
  >['getValues']
  const setCreditCardError = setError as UseFormMethods<FormValues.CreditCard>['setError']
  const setSavedCreditCardError = setError as UseFormMethods<FormValues.SavedCreditCard>['setError']

  // determine whether saved credit card is selected
  const selectedSavedCreditCardId = getCreditCardValues('selectedSavedCreditCardId')

  // introspect error messages
  const isExpirationError = paymentErrors.some((graphQLError) =>
    graphQLError.message.includes('expiry'),
  )

  if (isExpirationError) {
    if (selectedSavedCreditCardId) {
      // set error state on dropdown if saved credit card is selected
      setSavedCreditCardError('selectedSavedCreditCardId', {
        type: 'notMatch',
        message: 'Invalid Expiration Date',
      })
    } else {
      // set error state on expiration fields if saved credit card is not selected
      setCreditCardError('cardExpMonth', { type: 'notMatch', message: 'Invalid Expiration Date' })
      setCreditCardError('cardExpYear', { type: 'notMatch', message: 'Invalid Expiration Date' })
    }
    return
  }

  if (selectedSavedCreditCardId) {
    setSavedCreditCardError('selectedSavedCreditCardId', {})
    setSavedCreditCardError('cardCVV', {})
  } else {
    // set error on all payment fields if card could not be validated
    const fields: Array<keyof FormValues.CreditCard> = [
      'cardCVV',
      'cardExpMonth',
      'cardExpYear',
      'cardNumber',
      'city',
      'country_code',
      'firstname',
      'lastname',
      'postcode',
      'region',
      'street1',
      'street2',
      'telephone',
    ]

    fields.forEach((field) => {
      setCreditCardError(field, {})
    })
  }
}
